import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit, Trash2, Check, X, Loader2, Shield, 
  Mail, Phone, Calendar, Eye, EyeOff, UserPlus, Search,
  ToggleLeft, ToggleRight, Clock, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubAdminManagement = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSubAdmin, setEditingSubAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [subAdminsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/admin/sub-admins`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSubAdmins(subAdminsRes.data.sub_admins || []);
      setRoles(rolesRes.data.roles || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingSubAdmin(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role_id: ''
    });
    setShowPassword(false);
    setShowDialog(true);
  };

  const openEditDialog = (subAdmin) => {
    setEditingSubAdmin(subAdmin);
    setFormData({
      name: subAdmin.name,
      email: subAdmin.email,
      phone: subAdmin.phone || '',
      password: '',
      role_id: subAdmin.role_id
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.role_id) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!editingSubAdmin && !formData.password) {
      toast.error('Password is required for new sub-admin');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingSubAdmin) {
        const updateData = {
          name: formData.name,
          phone: formData.phone,
          role_id: formData.role_id
        };
        await axios.put(`${API}/admin/sub-admins/${editingSubAdmin.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sub-admin updated successfully');
      } else {
        await axios.post(`${API}/admin/sub-admins`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sub-admin created successfully');
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sub-admin');
    }
  };

  const toggleStatus = async (subAdmin) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/sub-admins/${subAdmin.id}`, {
        is_active: !subAdmin.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sub-admin ${subAdmin.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (subAdmin) => {
    if (!confirm(`Are you sure you want to delete "${subAdmin.name}"? This action cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/sub-admins/${subAdmin.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sub-admin deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-admin');
    }
  };

  const filteredSubAdmins = subAdmins.filter(sa => 
    sa.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sa.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sa.role_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-purple-600" />
            Sub-Admin Management
          </h2>
          <p className="text-slate-600 mt-1">Manage sub-administrators and their roles</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search sub-admins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-700">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Sub-Admin
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{subAdmins.length}</p>
              <p className="text-sm text-slate-500">Total Sub-Admins</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {subAdmins.filter(sa => sa.is_active).length}
              </p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <X className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {subAdmins.filter(sa => !sa.is_active).length}
              </p>
              <p className="text-sm text-slate-500">Disabled</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{roles.length}</p>
              <p className="text-sm text-slate-500">Roles Available</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Admins Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Sub-Admin</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Role</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Contact</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Last Login</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Status</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubAdmins.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-lg font-medium">No sub-admins found</p>
                    <p className="text-sm">Create your first sub-admin to get started</p>
                  </td>
                </tr>
              ) : (
                filteredSubAdmins.map(sa => (
                  <tr key={sa.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {sa.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{sa.name}</p>
                          <p className="text-sm text-slate-500">{sa.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {sa.role_name || 'No Role'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      {sa.phone ? (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-4 h-4" />
                          {sa.phone}
                        </div>
                      ) : (
                        <span className="text-slate-400">No phone</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1 text-slate-600 text-sm">
                        <Clock className="w-4 h-4" />
                        {formatDate(sa.last_login)}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => toggleStatus(sa)}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          sa.is_active 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {sa.is_active ? (
                          <><ToggleRight className="w-4 h-4" /> Active</>
                        ) : (
                          <><ToggleLeft className="w-4 h-4" /> Disabled</>
                        )}
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEditDialog(sa)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(sa)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              {editingSubAdmin ? 'Edit Sub-Admin' : 'Add New Sub-Admin'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                className="mt-1"
                disabled={editingSubAdmin}
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 9876543210"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="role">Assign Role *</Label>
              <select
                id="role"
                value={formData.role_id}
                onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select a role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>

            {!editingSubAdmin && (
              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editingSubAdmin ? 'Update' : 'Create'} Sub-Admin
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubAdminManagement;
